import React from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, User, Users, Monitor, MapPin } from "lucide-react";
import { EMAIL } from "@/data/content";
import { api } from "@/lib/api";
import { imgUrl } from "@/lib/api";
import { formatDateIT } from "@/lib/categories";
import { SectionHeader } from "@/pages/Home";

const GREEN = "#2FA84F";
const GREEN_DARK = "#25863F";

export default function Eventi() {
  const [tab, setTab] = React.useState("Tutti");
  const [events, setEvents] = React.useState([]);
  React.useEffect(() => { api.get("/events").then((r) => setEvents(r.data)).catch(console.error); }, []);
  const tabs = ["Tutti", "Webinar", "Workshop", "Seminario", "Open Day"];
  const visible = events.filter((e) => e.published !== false);
  const list = tab === "Tutti" ? visible : visible.filter((e) => e.type === tab);

  return (
    <div className="bg-white">
      <section className="relative overflow-hidden pt-16 sm:pt-24 pb-6" style={{ background: "linear-gradient(180deg,#f2fbf5 0%,#ffffff 100%)" }}>
        <div className="absolute -top-32 -right-24 w-[30rem] h-[30rem] rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(47,168,79,0.12)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Gratuiti & aperti a tutti" title="Eventi & Webinar" subtitle="Partecipa ai nostri eventi gratuiti, webinar e workshop. Formazione, ispirazione e networking." accent={GREEN} titleColor="#1a6b30" />
          <div className="mt-8 flex flex-wrap gap-2" data-testid="eventi-tabs">
            {tabs.map((t) => {
              const active = tab === t;
              return (
                <button key={t} data-testid={`eventi-tab-${t}`} onClick={() => setTab(t)}
                  className="px-4 py-2 rounded-full text-sm font-600 border transition-all hover:scale-[1.03]"
                  style={active ? { background: GREEN, color: "#fff", borderColor: GREEN } : { background: "#fff", color: "#6B7280", borderColor: "#E5E7EB" }}>
                  {t}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-28 pt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-testid="eventi-grid">
          {list.map((e, i) => {
            const online = e.modalita === "online";
            const mailto = `mailto:${EMAIL}?subject=${encodeURIComponent("Iscrizione evento: " + e.title)}`;
            const regLink = (e.link_iscrizione || "").trim();
            const hasLink = /^https?:\/\//i.test(regLink);
            const href = hasLink ? regLink : mailto;
            return (
              <motion.div key={e.id} layout initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
                data-testid={`evento-card-${e.id}`}
                className="bg-white rounded-3xl border border-gray-100 overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_rgba(47,168,79,0.12)]">
                <div className="relative h-40 overflow-hidden">
                  <img src={imgUrl(e.image)} alt={e.title} loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,rgba(37,134,63,0) 55%,rgba(37,134,63,0.45) 100%)" }} />
                  <span className="absolute top-3 left-3 text-[11px] font-700 px-2.5 py-1 rounded-full text-white" style={{ background: GREEN }}>{e.type}</span>
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[11px] font-600 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur" style={{ color: online ? "#1a6b30" : GREEN_DARK }}>
                    {online ? <Monitor size={12} /> : <MapPin size={12} />}{online ? "Online" : "In presenza"}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display font-700 text-lg text-[#111827] tracking-tight leading-snug">{e.title}</h3>
                  <p className="mt-2 text-sm text-gray-500 line-clamp-2">{e.description}</p>
                  <div className="mt-4 space-y-2 text-sm text-gray-500">
                    <div className="flex items-center gap-2.5"><User size={15} style={{ color: GREEN }} className="shrink-0" />{e.relatore}</div>
                    <div className="flex items-center gap-2.5"><Calendar size={15} style={{ color: GREEN }} className="shrink-0" />{formatDateIT(e.date)}</div>
                    <div className="flex items-center gap-2.5"><Clock size={15} style={{ color: GREEN }} className="shrink-0" />{e.orario}</div>
                    <div className="flex items-center gap-2.5"><Users size={15} style={{ color: GREEN }} className="shrink-0" />{e.posti} posti disponibili</div>
                  </div>
                  <a href={href} {...(hasLink ? { target: "_blank", rel: "noopener noreferrer" } : {})} data-testid={`evento-iscriviti-${e.id}`} className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-white font-600 transition-all hover:scale-[1.02] hover:brightness-95" style={{ background: GREEN }}>
                    Iscriviti gratis
                  </a>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
